import React, { useState, useRef, useEffect } from "react";
import "./Tooltip.css";

export type TooltipProps = {
  children: [React.ReactElement, React.ReactElement];
  delayDuration?: number;
  className?: string;
};

export const TooltipProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <>{children}</>;
};

export const Tooltip = ({
  children,
  delayDuration = 200,
  className = "",
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout>();
  const triggerRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [trigger, content] = children;

  const showTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delayDuration);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  const updatePosition = () => {
    if (triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      const top = triggerRect.top - tooltipRect.height - 8;
      const left =
        triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;

      setPosition({ top, left });
    }
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
    }
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div className={`ds2-tooltip-container ${className}`}>
      {React.cloneElement(trigger, {
        ref: triggerRef,
        onMouseEnter: showTooltip,
        onMouseLeave: hideTooltip,
        onFocus: showTooltip,
        onBlur: hideTooltip,
      })}

      {isVisible && (
        <div
          ref={tooltipRef}
          className="ds2-tooltip"
          style={{
            position: "fixed",
            top: position.top,
            left: position.left,
            zIndex: 9999,
          }}>
          {content}
        </div>
      )}
    </div>
  );
};

export const TooltipTrigger = ({
  children,
  asChild,
  ...props
}: {
  children: React.ReactNode;
  asChild?: boolean;
} & React.ComponentPropsWithoutRef<"div">) => {
  if (asChild) {
    return React.Children.only(children) as React.ReactElement;
  }

  return <div {...props}>{children}</div>;
};

export const TooltipContent = ({
  children,
  className = "",
  ...props
}: {
  children: React.ReactNode;
  className?: string;
} & React.ComponentPropsWithoutRef<"div">) => {
  return (
    <div className={`ds2-tooltip-content ${className}`} {...props}>
      {children}
    </div>
  );
};
